import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  LeadFormData,
  markLeadFormSubmitted,
  saveLeadFormData,
  submitLeadToCRM,
} from "@/lib/leadCapture"
import { toast } from "sonner"
import { Spinner } from "@blueprintjs/core"

interface LeadCaptureModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function LeadCaptureModal({
  isOpen,
  onClose,
  onSuccess,
}: LeadCaptureModalProps) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<LeadFormData>({
    name: "",
    companyName: "",
    email: "",
    phone: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Partial<LeadFormData>>({})

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<LeadFormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = t("planOrder.leadCapture.nameRequired")
    }

    if (!formData.email.trim()) {
      newErrors.email = t("planOrder.leadCapture.emailRequired")
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("planOrder.leadCapture.emailInvalid")
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, t])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!validateForm()) {
        return
      }

      setIsSubmitting(true)

      // Track form submission attempt
      window.analytics?.track("LeadCapture_SubmitAttempt", {
        name: formData.name,
        companyName: formData.companyName,
        hasPhone: Boolean(formData.phone),
      })

      try {
        const result = await submitLeadToCRM(formData)

        if (result) {
          saveLeadFormData(formData)
          markLeadFormSubmitted()

          // Track successful submission
          window.analytics?.track("LeadCapture_SubmitSuccess", {
            name: formData.name,
            companyName: formData.companyName,
            hasPhone: Boolean(formData.phone),
            leadId: typeof result === "string" ? result : undefined,
          })

          toast.success(t("planOrder.leadCapture.success"))
          onSuccess?.()
          onClose()
        } else {
          // Still mark as submitted to not annoy user
          markLeadFormSubmitted()

          window.analytics?.track("LeadCapture_SubmitFailed")

          // Don't show error toast, just close and let user use the app
          onClose()
        }
      } catch (error) {
        console.error("Lead submission error:", error)
        // Mark as submitted anyway to not block user
        markLeadFormSubmitted()

        window.analytics?.track("LeadCapture_SubmitError", {
          error: error instanceof Error ? error.message : "Unknown error",
        })

        onClose()
      } finally {
        setIsSubmitting(false)
      }
    },
    [formData, validateForm, onClose, onSuccess, t],
  )

  const handleInputChange = useCallback(
    (field: keyof LeadFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    },
    [errors],
  )

  // Prevent any closing - user must submit the form
  const preventClose = useCallback((e: Event) => {
    e.preventDefault()
  }, [])

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="max-w-md bg-[#FAFAFA] sm:max-w-lg [&>button]:hidden"
        onPointerDownOutside={preventClose}
        onEscapeKeyDown={preventClose}
        onInteractOutside={preventClose}
      >
        <DialogHeader className="space-y-3 text-center">
          <DialogTitle className="text-2xl font-bold text-gray-900 sm:text-3xl">
            {t("planOrder.leadCapture.title")}
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600">
            {t("planOrder.leadCapture.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="text-sm font-normal text-gray-600"
                >
                  {t("planOrder.contactForm.name")}
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange("name")}
                  className={`h-12 rounded-lg border-0 bg-gray-100 text-base ${
                    errors.name ? "ring-2 ring-red-500" : ""
                  }`}
                  placeholder=""
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="companyName"
                  className="text-sm font-normal text-gray-600"
                >
                  {t("planOrder.contactForm.companyName")}
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleInputChange("companyName")}
                  className="h-12 rounded-lg border-0 bg-gray-100 text-base"
                  placeholder=""
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-sm font-normal text-gray-600"
                >
                  {t("planOrder.contactForm.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  className={`h-12 rounded-lg border-0 bg-gray-100 text-base ${
                    errors.email ? "ring-2 ring-red-500" : ""
                  }`}
                  placeholder=""
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="phone"
                  className="text-sm font-normal text-gray-600"
                >
                  {t("planOrder.contactForm.phone")}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange("phone")}
                  className="h-12 rounded-lg border-0 bg-gray-100 text-base"
                  placeholder=""
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 h-12 w-full rounded-lg bg-blue-600 text-base font-medium text-white hover:bg-blue-700"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Spinner size={16} />
                  <span>{t("planOrder.contactForm.sending")}</span>
                </div>
              ) : (
                t("planOrder.leadCapture.submit")
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
